import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import type {NativeSelectionMap} from '@github-ui/taillights-symbols/native-selection'
import {type RefObject, useEffect, useEffectEvent, useRef} from 'react'

import {useIsFirefox} from '../../utilities/lines'

// A page scroll that has gone this long without a scroll event has ended, or never started at a document edge.
const settleMs = 100
// Safari fires scrollend at every step of a smooth keyboard scroll, so a scrollend only ends one that stays quiet.
const scrollEndSettleMs = 34
const pageDirections: Partial<Record<string, 1 | -1>> = {PageDown: 1, PageUp: -1}
const modifierKeys = new Set(['Alt', 'AltGraph', 'CapsLock', 'Control', 'Fn', 'Meta', 'Shift'])
// Chrome pages by the viewport height less this overlap; Space pages by the same step.
const pageOverlap = 20

type PageMove = {
  direction: 1 | -1
  startScrollY: number
  // Whether the page has left startScrollY, so a sequence that scrolls back to an edge isn't taken as stuck there.
  scrolled: boolean
  caret: number
  timer?: number
}

/**
 * The page a key moves by. Page Up and Page Down keep the browser's own page scroll, so they aren't shortcuts; a
 * ui-commands handler would cancel the key and stop that scroll. Firefox keeps those keys in the textarea, which can't
 * scroll, so they page like Space there. Shift+Page keys don't scroll the page and keep their native behavior. Space and
 * Shift+Space page as they do outside text fields, which this editable textarea prevents.
 */
function pageKey(event: KeyboardEvent, nativePageKeys: boolean): {direction: 1 | -1; native: boolean} | undefined {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- See above: follows the native page scroll.
  if (event.altKey || event.ctrlKey || event.metaKey) return undefined
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- See above: Space pages like Page Down.
  if (event.key === ' ') {
    // Space is a character key, so it pages only while character key shortcuts are enabled, like the production viewer.
    if (!areCharacterKeyShortcutsEnabled()) return undefined
    return {direction: event.shiftKey ? -1 : 1, native: false}
  }
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- See above: follows the native page scroll.
  const direction = event.shiftKey ? undefined : pageDirections[event.key]
  return direction && {direction, native: nativePageKeys}
}

/** Whether a key only modifies others, so pressing it doesn't replace a pending page move. */
function isModifierKey(event: KeyboardEvent) {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Classifies keys; handles none.
  return modifierKeys.has(event.key)
}

function rowAt(lineStarts: readonly number[], offset: number) {
  let low = 0
  let high = lineStarts.length - 1
  while (low < high) {
    const middle = (low + high + 1) >>> 1
    if ((lineStarts[middle] ?? 0) <= offset) low = middle
    else high = middle - 1
  }
  return low
}

/**
 * Moves the native caret with Page Up and Page Down, and pages with Space and Shift+Space. The textarea spans the whole
 * source and can't scroll, so the browser scrolls the page but leaves the caret behind. The page scroll stays native;
 * once it settles, the caret moves by the rows the page moved, keeping its place on screen, and lands in view when it
 * started out of view. At the top or bottom of the page, where it can't scroll further, the caret moves to the first or
 * last line.
 */
export function usePageCaret({
  textareaRef,
  projection,
  scrollMargin,
  lineHeight,
  scrollPaddingTop,
}: {
  textareaRef: RefObject<HTMLTextAreaElement | null>
  projection: NativeSelectionMap
  scrollMargin: number
  lineHeight: number
  scrollPaddingTop: number
}) {
  const moveRef = useRef<PageMove | null>(null)
  const nativePageKeys = !useIsFirefox()

  const settle = useEffectEvent(() => {
    const move = moveRef.current
    const textarea = textareaRef.current
    moveRef.current = null
    if (!move || !textarea || document.activeElement !== textarea) return
    const {lineStarts, lines} = projection
    const lastRow = lines.length - 1
    const row = rowAt(lineStarts, move.caret)
    const column = move.caret - (lineStarts[row] ?? 0)
    const scrolledRows = Math.round((window.scrollY - move.startScrollY) / lineHeight)
    const maxScrollY = document.documentElement.scrollHeight - window.innerHeight
    const atEdge = move.direction > 0 ? window.scrollY >= maxScrollY - 1 : window.scrollY <= 0
    let target: number
    if (scrolledRows === 0) {
      if (move.scrolled || !atEdge) return
      target = move.direction > 0 ? lastRow : 0
    } else {
      const top = window.scrollY + scrollPaddingTop - scrollMargin
      const firstVisible = Math.max(0, Math.ceil(top / lineHeight))
      const lastVisible = Math.min(
        lastRow,
        Math.floor((window.scrollY + window.innerHeight - scrollMargin) / lineHeight) - 1,
      )
      target = Math.min(Math.max(row + scrolledRows, firstVisible), Math.max(firstVisible, lastVisible))
    }
    target = Math.min(Math.max(target, 0), lastRow)
    const caret = (lineStarts[target] ?? 0) + Math.min(column, lines[target]?.length ?? 0)
    textarea.setSelectionRange(caret, caret)
  })

  const scheduleSettle = useEffectEvent((delay = settleMs) => {
    const move = moveRef.current
    if (!move) return
    window.clearTimeout(move.timer)
    move.timer = window.setTimeout(settle, delay)
  })

  // Other navigation or a pointer selection replaces a page move that hasn't settled yet.
  const cancel = useEffectEvent(() => {
    window.clearTimeout(moveRef.current?.timer)
    moveRef.current = null
  })

  const onKeyDown = useEffectEvent((event: KeyboardEvent) => {
    const key = pageKey(event, nativePageKeys)
    const textarea = textareaRef.current
    if (!key || !textarea || event.isComposing) {
      if (!isModifierKey(event)) cancel()
      return
    }
    const {direction} = key
    // Read before paging: an instant scroll (reduced motion) has already moved when scrollBy returns.
    const startScrollY = window.scrollY
    if (!key.native) {
      // Page as the browser would; the textarea's value is controlled, so a space wouldn't insert anyway.
      event.preventDefault()
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const step = document.documentElement.clientHeight - pageOverlap
      window.scrollBy({top: direction * step, behavior: reduce ? 'instant' : 'smooth'})
    }
    const pending = moveRef.current
    // Presses during one scroll, in either direction, move the caret once by the net distance from where it started.
    if (pending) {
      pending.direction = direction
      if (startScrollY !== pending.startScrollY || window.scrollY !== pending.startScrollY) pending.scrolled = true
      scheduleSettle()
      return
    }
    // A selection collapses to its moving end, as arrow keys collapse it.
    const caret = textarea.selectionDirection === 'backward' ? textarea.selectionStart : textarea.selectionEnd
    moveRef.current = {direction, startScrollY, scrolled: window.scrollY !== startScrollY, caret}
    scheduleSettle()
  })

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    const onScroll = () => {
      const move = moveRef.current
      if (!move) return
      if (window.scrollY !== move.startScrollY) move.scrolled = true
      scheduleSettle()
    }
    // The end of an earlier scroll, such as a wheel fling, can arrive before the page scroll starts.
    const onScrollEnd = () => {
      if (moveRef.current && window.scrollY !== moveRef.current.startScrollY) scheduleSettle(scrollEndSettleMs)
    }
    textarea.addEventListener('keydown', onKeyDown)
    textarea.addEventListener('pointerdown', cancel)
    // eslint-disable-next-line github/prefer-observers -- A page scroll's progress is only observable through scroll events.
    window.addEventListener('scroll', onScroll)
    window.addEventListener('scrollend', onScrollEnd)
    return () => {
      textarea.removeEventListener('keydown', onKeyDown)
      textarea.removeEventListener('pointerdown', cancel)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('scrollend', onScrollEnd)
      window.clearTimeout(moveRef.current?.timer)
      moveRef.current = null
    }
  }, [textareaRef])
}
